'use client';

import titanXLogo from '@/images/logos/TitanX_White-Red_New.svg';
import {
  SESSION_END_REASON_PARAM,
  sessionEndMessageFor,
} from '@/lib/auth/session-end-reason';
import { createClient } from '@/utils/supabase/client';
import { Button, Card, Input, PasswordInput } from '@apex-titan/ui';
import { Turnstile, type TurnstileInstance } from '@marsidev/react-turnstile';
import { AnimatePresence, motion } from 'framer-motion';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import { Controller, useForm, useFormState } from 'react-hook-form';

import { Form } from '@/components/form';
import { applyActionResult } from '@/lib/forms/apply-action-result';

import type { SignInPageData } from './page';
import { signInResolver, type SignInValues } from './sign-in-schema';

export const SignInContent = ({
  bannerMessage,
  shouldRedirectToSSO,
  signInWithPassword,
}: SignInPageData) => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const code = searchParams.get('code');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(!!code);
  const turnstileRef = useRef<TurnstileInstance>(null);

  const form = useForm<SignInValues>({
    resolver: signInResolver,
    defaultValues: { email: '', password: '', turnstileToken: '' },
  });
  const { errors } = useFormState({ control: form.control });
  const rootError = errors.root?.serverError?.message;

  const sessionEndMessage = sessionEndMessageFor(
    searchParams.get(SESSION_END_REASON_PARAM)
  );
  useEffect(() => {
    if (sessionEndMessage) {
      form.setError('root.serverError', {
        type: 'server',
        message: sessionEndMessage,
      });
    }
  }, [form, sessionEndMessage]);

  // Register the Turnstile token as a hidden field (no visible input). Its
  // value is set imperatively from the Turnstile callbacks below.
  useEffect(() => {
    form.register('turnstileToken');
  }, [form]);

  const emailValue = form.watch('email');
  const passwordValue = form.watch('password');
  const turnstileToken = form.watch('turnstileToken');

  const handleSSOLogin = async (email: string) => {
    const domain = email.split('@')[1];
    const supabase = createClient();

    const { data, error } = await supabase.auth.signInWithSSO({
      domain,
      options: {
        redirectTo: `${window.location.origin}/authentication/sign-in`,
      },
    });

    if (data?.url) {
      window.location.href = data.url;
      return true;
    } else {
      form.setError('root.serverError', {
        type: 'server',
        message: error?.message || 'Could not start SSO login.',
      });
      setLoading(false);
      return false;
    }
  };
  useEffect(() => {
    if (!code) return;

    const handleCodeExchange = async () => {
      try {
        const supabase = createClient();
        const { error: exchangeError } =
          await supabase.auth.exchangeCodeForSession(window.location.href);

        // Strip code/state so a refresh returns to a clean form.
        const cleanUrl = new URL(window.location.href);
        cleanUrl.searchParams.delete('code');
        cleanUrl.searchParams.delete('state');
        window.history.replaceState({}, document.title, cleanUrl.pathname);

        if (exchangeError) throw exchangeError;

        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (!session) throw new Error('Your session could not be established.');

        const res = await fetch('/api/auth/handle-sso-login', {
          method: 'POST',
          body: JSON.stringify({}),
        });
        const result = await res.json();

        if (res.ok && result.redirectTo) {
          router.push(result.redirectTo);
          return;
        }
        throw new Error(result?.error ?? 'Could not complete sign-in.');
      } catch (err) {
        form.setError('root.serverError', {
          type: 'server',
          message:
            err instanceof Error
              ? err.message
              : 'Could not complete sign-in. Please try again.',
        });
        setLoading(false);
      }
    };

    handleCodeExchange();
  }, [code, router, form]);

  const handleEmailContinue = async (e: React.FormEvent) => {
    e.preventDefault();
    form.clearErrors('root.serverError');

    const ok = await form.trigger('email');
    if (!ok) return;

    setLoading(true);
    const email = form.getValues('email');

    const shouldSSO = await shouldRedirectToSSO(email);
    if (shouldSSO) {
      const success = await handleSSOLogin(email);
      if (success) return;
    }

    setShowPassword(true);
    setLoading(false);
  };
  const handleEmailBlur = async () => {
    if (!showPassword) return;

    const email = form.getValues('email');
    const shouldSSO = await shouldRedirectToSSO(email);
    if (shouldSSO) {
      setLoading(true);
      await handleSSOLogin(email);
    }
  };

  const handlePasswordLogin = form.handleSubmit(async (values) => {
    form.clearErrors('root.serverError');
    setLoading(true);

    const fd = new FormData();
    fd.append('email', values.email);
    fd.append('password', values.password);
    fd.append('cf-turnstile-response', values.turnstileToken);

    const response = await signInWithPassword(fd);

    if (response?.status === 200) {
      if (response.requiresMFA) {
        // User needs MFA verification
        router.push('/authentication/mfa-verify');
      } else {
        // Server-resolved first enterable section (fallback: resolver).
        router.push(response.redirectTo ?? '/');
      }
      return;
    }

    const message =
      response?.status === 400 || response?.status === 401
        ? (response?.message ?? 'Login failed.')
        : 'Something went wrong. Please try again.';
    applyActionResult(form, { ok: false, formError: message });
    setLoading(false);
    // Reset Turnstile for a fresh token (tokens are single-use).
    form.setValue('turnstileToken', '');
    turnstileRef.current?.reset();
  });

  const handleTurnstileSuccess = (token: string) => {
    form.setValue('turnstileToken', token, { shouldValidate: true });
  };

  const handleTurnstileExpire = () => {
    form.setValue('turnstileToken', '');
  };

  const handleTurnstileError = () => {
    form.setError('root.serverError', {
      type: 'server',
      message: 'Security verification unavailable. Please refresh the page.',
    });
  };

  // Focus the password field once it is revealed. Small delay to ensure the
  // animation has started.
  useEffect(() => {
    if (!showPassword) return;
    const timer = setTimeout(() => form.setFocus('password'), 100);
    return () => clearTimeout(timer);
  }, [showPassword, form]);

  return (
    <div className="relative z-0 flex min-h-dvh items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        <Link
          href="/"
          className="mb-8 flex items-center justify-center"
          aria-label="TitanX home"
        >
          <Image
            src={titanXLogo}
            alt="TitanX"
            priority
            className="h-6.75 w-auto"
          />
        </Link>

        <Card className="w-full">
          {bannerMessage && (
            <div className="bg-ds-surface-secondary rounded-lg p-4">
              <p className="text-ds-text-primary font-semibold">
                {bannerMessage.header}
              </p>
              <p className="text-ds-text-secondary text-sm">
                {bannerMessage.body}
              </p>
            </div>
          )}

          <div className="flex flex-col gap-6">
            <h2 className="text-ds-text-primary text-2xl font-bold lg:text-3xl">
              Sign in
            </h2>

            <Form
              onSubmit={
                showPassword ? handlePasswordLogin : handleEmailContinue
              }
              className="flex flex-col gap-5"
            >
              <motion.div
                key="email-input"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
                <Controller
                  control={form.control}
                  name="email"
                  render={({ field, fieldState }) => (
                    <Input
                      {...field}
                      value={field.value ?? ''}
                      onBlur={() => {
                        field.onBlur();
                        handleEmailBlur();
                      }}
                      id="email"
                      label="Your email"
                      disabled={loading}
                      placeholder="you@company.com"
                      type="email"
                      autoComplete="username"
                      errorMessage={fieldState.error?.message}
                    />
                  )}
                />
              </motion.div>

              <AnimatePresence>
                {showPassword && (
                  <motion.div
                    key="password-input"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 8 }}
                    transition={{ duration: 0.3 }}
                  >
                    <Controller
                      control={form.control}
                      name="password"
                      render={({ field, fieldState }) => (
                        <PasswordInput
                          {...field}
                          value={field.value ?? ''}
                          id="password"
                          label="Your password"
                          disabled={loading}
                          placeholder="••••••••"
                          autoComplete="current-password"
                          errorMessage={fieldState.error?.message}
                        />
                      )}
                    />
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Turnstile widget - only shown when password field is visible */}
              {showPassword && (
                <div className="flex flex-col gap-2">
                  <span className="text-ds-text-secondary text-sm font-medium">
                    Let us know you are human
                  </span>
                  <Turnstile
                    ref={turnstileRef}
                    siteKey={process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY!}
                    onSuccess={handleTurnstileSuccess}
                    onExpire={handleTurnstileExpire}
                    onError={handleTurnstileError}
                    options={{ theme: 'dark', size: 'flexible' }}
                  />
                </div>
              )}

              <AnimatePresence>
                {rootError && (
                  <motion.div
                    key="error"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 8 }}
                    transition={{ duration: 0.3 }}
                    role="alert"
                    className="rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-2.5 text-sm text-red-400"
                  >
                    {rootError}
                  </motion.div>
                )}
              </AnimatePresence>

              <Button
                variant="primary"
                size="lg"
                type="submit"
                loading={loading}
                disabled={
                  showPassword &&
                  (!emailValue.trim() || !passwordValue || !turnstileToken)
                }
                className="w-full"
              >
                {showPassword ? 'Login to your account' : 'Continue'}
              </Button>

              <AnimatePresence>
                {showPassword && (
                  <motion.div
                    key="extras"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="text-center"
                  >
                    <Link
                      href="/authentication/forgot-password"
                      className="text-sm font-medium text-red-500 hover:underline"
                    >
                      Lost Password?
                    </Link>
                  </motion.div>
                )}
              </AnimatePresence>
            </Form>
          </div>
        </Card>
      </div>
    </div>
  );
};
